// components/Ads/AdSenseOptimized.tsx
import React, { useEffect, useRef, useState } from 'react';
import { AD_CLIENT_ID } from './adConfig';

interface AdSenseOptimizedProps {
  slot: string;
  format?: 'auto' | 'fluid' | 'rectangle' | 'leaderboard';
  className?: string;
  fallbackContent?: React.ReactNode;
}

const AdSenseOptimized: React.FC<AdSenseOptimizedProps> = ({
  slot,
  format = 'auto',
  className = '',
  fallbackContent
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [adLoaded, setAdLoaded] = useState(false);
  const [adError, setAdError] = useState(false);
  const mountedRef = useRef(false);
  const adElementId = useRef(`ad-${slot}-${Date.now()}`);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || adLoaded || adError) return;

    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const initializeAd = () => {
      try {
        if (!containerRef.current || !mountedRef.current) return;

        const container = containerRef.current;
        const adId = adElementId.current;

        if (document.getElementById(adId)) return;

        const wrapper = document.createElement('div');
        wrapper.id = `wrapper-${adId}`;
        wrapper.style.width = '100%';
        wrapper.style.height = '100%';

        const insElement = document.createElement('ins');
        insElement.id = adId;
        insElement.className = 'adsbygoogle';
        insElement.style.display = 'block';
        insElement.style.overflow = 'hidden';

        if (format === 'leaderboard') {
          insElement.style.minHeight = '90px';
          insElement.style.maxWidth = '728px';
        } else if (format === 'rectangle') {
          insElement.style.minHeight = '250px';
          insElement.style.maxWidth = '300px';
        } else {
          insElement.style.minHeight = '100px';
        }

        insElement.setAttribute('data-ad-client', AD_CLIENT_ID);
        insElement.setAttribute('data-ad-slot', slot);
        insElement.setAttribute('data-ad-format', format);
        insElement.setAttribute('data-full-width-responsive', 'true');

        wrapper.appendChild(insElement);
        container.appendChild(wrapper);

        const pushAd = (attempt = 0) => {
          if (!mountedRef.current) return;

          if (window.adsbygoogle) {
            try {
              (window.adsbygoogle = window.adsbygoogle || []).push({});
              setTimeout(() => {
                if (!mountedRef.current) return;
                const adElement = document.getElementById(adId);
                if (adElement) {
                  setAdLoaded(true);
                } else {
                  setAdError(true);
                }
              }, 2000);
            } catch (error) {
              console.warn('Ad push warning:', error);
              if (mountedRef.current) setAdError(true);
            }
            return;
          }

          if (attempt < 5) {
            retryTimer = setTimeout(() => pushAd(attempt + 1), 250);
          } else if (mountedRef.current) {
            setAdError(true);
          }
        };

        setTimeout(() => pushAd(0), 100);
      } catch (error) {
        console.error('Ad initialization error:', error);
        if (mountedRef.current) setAdError(true);
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setTimeout(initializeAd, 100);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (containerRef.current) observer.observe(containerRef.current);

    const timeoutId = setTimeout(() => {
      if (mountedRef.current) {
        initializeAd();
        observer.disconnect();
      }
    }, 2000);

    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [slot, format, adLoaded, adError]);

  if (adError) {
    return (
      <div
        ref={containerRef}
        className={`ad-container ${className}`}
        style={{
          width: '100%',
          backgroundColor: '#f0f0f0',
          border: '1px dashed #ccc',
          borderRadius: '4px',
          padding: '20px',
          textAlign: 'center',
          minHeight: format === 'leaderboard' ? '90px' : '250px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px'
        }}
      >
        {fallbackContent || (
          <>
            <div style={{ color: '#666', fontSize: '14px' }}>Ad could not load</div>
            <div style={{ fontSize: '11px', color: '#888' }}>Slot: {slot}</div>
          </>
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`ad-container ${className}`}
      style={{
        width: '100%',
        minHeight: format === 'leaderboard' ? '90px' : (format === 'rectangle' ? '250px' : '100px'),
        backgroundColor: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        overflow: 'hidden',
        position: 'relative',
        margin: '0 auto',
        ...(format === 'rectangle' && { maxWidth: '300px' }),
        ...(format === 'leaderboard' && { maxWidth: '728px' })
      }}
    >
      {!adLoaded && !adError && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: '#666',
            fontSize: '13px',
            backgroundColor: '#f9fafb'
          }}
        >
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            Loading ad...
          </div>
        </div>
      )}
    </div>
  );
};

export const resetAdTracking = () => {};

export default AdSenseOptimized;
